// Lecture côté client de l'état des batches d'extraction async (`llm_batches`).
//
// Sert à l'UX « extraction limpide » : si une rencontre est partie en Batch API
// (OpenAI, ~min-h), on veut afficher « 🕒 en file » plutôt que « à reprendre ».
// La lecture est autorisée par la policy `llm_batches_read` (authenticated,
// table technique sans PII).
//
// Un batch est « actif » tant que son status est `submitted` ou `in_progress`.

import type { SupabaseClient } from "@supabase/supabase-js";

const ACTIVE_BATCH_STATUSES = ["submitted", "in_progress"] as const;

// Batch actif (submitted/in_progress) d'une capture, ou `null`. `submittedAt`
// sert à borner l'attente « En file » (SIG-261002-082836) : un lot soumis il y
// a plus de 24 h ne répondra plus. Tolérant : en cas d'erreur RLS / réseau on
// retourne `null` (l'absence de batch est le défaut sûr — au pire on n'affiche
// pas « en file »).
export interface ActiveBatch {
  submittedAt: string | null;
}

export async function fetchActiveBatch(
  client: SupabaseClient,
  captureId: string,
): Promise<ActiveBatch | null> {
  const { data, error } = await client
    .from("llm_batches")
    .select("capture_id, submitted_at")
    .eq("capture_id", captureId)
    .in("status", [...ACTIVE_BATCH_STATUSES])
    .maybeSingle();
  if (error || !data) return null;
  const submittedAt = (data as { submitted_at?: unknown }).submitted_at;
  return { submittedAt: typeof submittedAt === "string" ? submittedAt : null };
}

// Variante groupée pour la LISTE : un seul appel pour N captures (évite N+1).
// Retourne l'ensemble des capture_id ayant un batch actif. Tolérant comme
// ci-dessus (Set vide en cas d'erreur).
export async function fetchActiveBatchCaptureIds(
  client: SupabaseClient,
  captureIds: string[],
): Promise<Set<string>> {
  if (captureIds.length === 0) return new Set();
  const { data, error } = await client
    .from("llm_batches")
    .select("capture_id")
    .in("capture_id", captureIds)
    .in("status", [...ACTIVE_BATCH_STATUSES]);
  if (error || !data) return new Set();
  return new Set((data as Array<{ capture_id: string }>).map((r) => r.capture_id));
}
